import { useEffect, useMemo, useState, useCallback } from "react";
import { useNearpayReceipts } from "@/hooks/useNearpayReceipts";
import Button from "@/components/ui/Button";
import { toast } from "react-toastify";
import { getApiInstance } from "@/utils/api";

type CardProps = {
  id: string;
  created_at: string;
  receiptId: string;
  url: string;
  amount: string;
  status: string;
  invoice: number | null;
  creating?: boolean;
  onCreateInvoice?: (trnId: string) => void;
};

function ReceiptCard({
  id,
  created_at,
  receiptId,
  url: _url,
  amount,
  status,
  invoice,
  creating,
  onCreateInvoice,
}: CardProps) {
  // عرض بطاقة مختصرة متجاوبة مع تفاصيل واضحة
  const createdDate = new Date(created_at);
  const datePart = createdDate.toLocaleDateString();
  let rawTime = createdDate.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const timePart = rawTime
    .replace(/\s?AM$/i, " صباحاً")
    .replace(/\s?PM$/i, " مساءً");
  // تم إزالة زر الطباعة بناءً على الطلب

  return (
    <div className='border rounded-xl p-3 sm:p-4 flex flex-col gap-3 bg-white shadow-sm'>
      <div className='flex items-center justify-end'>
        <span
          className={`px-2 py-1 rounded-full text-xs sm:text-sm ${
            status === "Approved"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {status}
        </span>
      </div>

      <div className='grid grid-cols-2 gap-x-4 gap-y-2 text-right'>
        <div className='text-gray-500 text-xs sm:text-sm'>رقم المعاملة</div>
        <div
          className='font-semibold text-sm sm:text-base font-mono break-all'
          title={id}
        >
          {id}
        </div>

        <div className='text-gray-500 text-xs sm:text-sm'>تاريخ الإنشاء</div>
        <div className='text-sm sm:text-base'>{datePart}</div>

        <div className='text-gray-500 text-xs sm:text-sm'>وقت الإنشاء</div>
        <div className='text-sm sm:text-base'>{timePart}</div>

        <div className='text-gray-500 text-xs sm:text-sm'>رقم الإيصال</div>
        <div
          className='font-medium text-sm sm:text-base font-mono break-all'
          title={receiptId}
        >
          {receiptId}
        </div>

        <div className='text-gray-500 text-xs sm:text-sm'>المبلغ</div>
        <div className='font-bold text-sm sm:text-base'>{amount} ريال</div>
      </div>

      {/* زر إنشاء الفاتورة يظهر أسفل البطاقة وفق الشروط */}
      {invoice === null && status === "Approved" ? (
        <div className='flex justify-end'>
          <Button
            type='primary'
            className='px-3 py-1.5 text-xs sm:text-sm'
            onClick={() => onCreateInvoice && onCreateInvoice(id)}
            disabled={creating}
          >
            {creating ? "جاري الإنشاء..." : "إنشاء فاتورة"}
          </Button>
        </div>
      ) : invoice ? (
        <div className='text-right text-xs sm:text-sm text-gray-500'>
          فاتورة #{invoice}
        </div>
      ) : null}
    </div>
  );
}

export default function NearpayReceiptsPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useNearpayReceipts(page);
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const api = getApiInstance();

  useEffect(() => {
    if (isError) toast.error("فشل في جلب إيصالات الدفع");
  }, [isError]);

  const items = useMemo(() => {
    const results = data?.results || [];
    return results
      .map((t) => {
        const r = t.receipts?.[0];
        if (!r) return null;
        return {
          id: t.id,
          created_at: t.created_at,
          receiptId: r.id,
          url: r.url,
          amount: r.amount_authorized,
          status: r.status,
          invoice: t.invoice,
        } as CardProps;
      })
      .filter(Boolean) as CardProps[];
  }, [data]);

  // Create invoice from transaction id handler
  const handleCreateInvoice = useCallback(
    async (trnId: string) => {
      try {
        setCreatingId(trnId);
        await api.post("/store/invoices/create_invoice_from_trn/", {
          trn_id: trnId,
        });
        toast.success("تم إنشاء الفاتورة بنجاح");
        // Refresh list to reflect new invoice
        await refetch();
      } catch (e: any) {
        // Extract a readable message when possible
        const msg =
          e?.response?.data?.message ||
          (typeof e?.response?.data === "string" ? e.response.data : null) ||
          "فشل إنشاء الفاتورة";
        toast.error(msg);
      } finally {
        setCreatingId(null);
      }
    },
    [api, refetch]
  );

  return (
    <div
      className={`max-w-5xl mx-auto ${isLoading ? "opacity-70" : ""} mb-10`}
      dir='rtl'
    >
      <h2 className='text-2xl font-bold mb-4 px-3 sm:px-0'>إيصالات الدفع</h2>
      <div className='px-3 sm:px-0'>
        {isLoading ? (
          <div className='text-center py-10'>جاري التحميل...</div>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
            {items.map((it) => (
              <ReceiptCard
                key={it.id}
                {...it}
                creating={creatingId === it.id}
                onCreateInvoice={handleCreateInvoice}
              />
            ))}
          </div>
        )}
      </div>
      <div className='mt-6 px-3 sm:px-0 flex flex-col gap-2'>
        <div className='flex items-center justify-center gap-8'>
          <Button
            type='secondary'
            className='px-3 py-1.5 text-xs sm:text-sm min-w-0 w-auto whitespace-nowrap'
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            السابق
          </Button>
          <div className='text-xs sm:text-sm font-bold text-gray-800'>
            الصفحة{" "}
            <span className='text-red-700'>{data?.pages?.current || page}</span>
            <span className='mx-1'>/</span>
            <span className='text-gray-900'>{data?.pages?.total || "-"}</span>
          </div>
          <Button
            type='secondary'
            className='px-3 py-1.5 text-xs sm:text-sm min-w-0 w-auto whitespace-nowrap'
            onClick={() => setPage((p) => p + 1)}
          >
            التالي
          </Button>
        </div>
      </div>
    </div>
  );
}
